<form [formGroup]="form" class="member-form">
  <!-- Read-only Member Unique ID (post-save only) -->
  @if (memberUniqueId) {
    <div class="uid-line">
      Member Unique ID: <b>{{ memberUniqueId || '—' }}</b>
    </div>
  }

  <!-- ===================== Policy & premium ===================== -->
  <h3 class="section-title"><i class="pi pi-shield"></i> Policy &amp; Premium</h3>

  <div class="field">
    <label>Premium Rule <span class="req">*</span></label>
    <p-select [options]="premiumRuleOptions" optionLabel="label" optionValue="value"
      formControlName="primiumrulrID" placeholder="Select a premium rule" appendTo="body"
      [filter]="true" filterBy="label" />
    @if (form.get('primiumrulrID')?.touched && form.get('primiumrulrID')?.invalid) {
      <small class="err">Please select a premium rule</small>
    }
  </div>

  <!-- Read-only policy details bound from the selected rule -->
  @if (form.get('masterPolicyName')?.value) {
    <div class="grid-3">
      <div class="field">
        <label>Master Policy</label>
        <input pInputText type="text" formControlName="masterPolicyName" readonly />
      </div>
      <div class="field">
        <label>Coverage Type</label>
        <input pInputText type="text" formControlName="coverageType" readonly />
      </div>
      <div class="field">
        <label>Premium Code</label>
        <input pInputText type="text" formControlName="premiumCode" readonly />
      </div>
    </div>
  }

  <!-- ===================== Member details ===================== -->
  <h3 class="section-title"><i class="pi pi-user"></i> Member Details</h3>

  <div class="grid-2">
    <div class="field">
      <label>First Name <span class="req">*</span></label>
      <input pInputText type="text" formControlName="firstName" placeholder="First name" />
      @if (form.get('firstName')?.touched && form.get('firstName')?.invalid) {
        <small class="err">First name is required</small>
      }
    </div>
    <div class="field">
      <label>Middle Name</label>
      <input pInputText type="text" formControlName="middleName" placeholder="Middle name" />
    </div>
  </div>

  <div class="grid-2">
    <div class="field">
      <label>Last Name</label>
      <input pInputText type="text" formControlName="lastName" placeholder="Last name" />
    </div>
    <div class="field">
      <label>Full Name</label>
      <input pInputText type="text" formControlName="fullName" readonly placeholder="Auto-generated" />
      <small class="hint-muted">Auto-generated</small>
    </div>
  </div>

  <!-- Loan -->
  <div class="field">
    <label>Loan Number/ UniqueId</label>
    <input pInputText type="text" formControlName="loanNumber" placeholder="Loan number" />
    <small class="hint-muted">Leave blank to auto-generate</small>
  </div>

  <!-- DOB / Age -->
  <div class="grid-2">
    <div class="field">
      <label>Date of Birth <span class="req">*</span></label>
      <!-- Bounded by the selected plan's age band: dates that would make the member ineligible
           are greyed out, so the range is discovered while picking rather than on save.
           Falls back to "any past date" until a rule is chosen. -->
      <p-datepicker formControlName="dateOfBirth" dateFormat="dd M yy" [showIcon]="true"
        iconDisplay="input" [readonlyInput]="true" [minDate]="dobMin" [maxDate]="dobMax"
        [defaultDate]="dobMax" appendTo="body"
        placeholder="Select date of birth" />
      @if (form.get('dateOfBirth')?.touched && form.get('dateOfBirth')?.hasError('required')) {
        <small class="err">Date of birth is required</small>
      }
      @if (selectedRule) {
        <small class="hint-muted">Only dates within this plan's age range can be selected</small>
      }
    </div>
    <div class="field">
      <label>Age</label>
      <input pInputText type="number" formControlName="age" readonly placeholder="—"
        [class.age-bad]="!!ageError" [class.age-ok]="currentAge != null && selectedRule && !ageError" />
      <!-- Live verdict: the age band is known as soon as a plan is picked, so eligibility is
           answered here instead of by the server on save. -->
      @if (ageMessage) {
        <small [class]="ageError ? 'err' : 'ok-hint'">{{ ageMessage }}</small>
      } @else if (selectedRangeHint) {
        <small class="hint-muted">{{ selectedRangeHint }}</small>
      } @else {
        <small class="hint-muted">Calculated from date of birth</small>
      }
    </div>
  </div>

  <!-- Gender -->
  <div class="grid-2">
    <div class="field">
      <label>Gender</label>
      <p-select [options]="genderOptions" formControlName="gender" placeholder="Select gender"
        appendTo="body" />
    </div>
    <div class="field">
      <label>PAN Number</label>
      <input pInputText type="text" formControlName="panNumber" placeholder="ABCDE1234F" maxlength="10" />
      @if (form.get('panNumber')?.touched && form.get('panNumber')?.invalid) {
        <small class="err">Invalid PAN (e.g. ABCDE1234F)</small>
      } @else {
        <small class="hint-muted">Optional</small>
      }
    </div>
  </div>

  <!-- Aadhaar — the KYC identifier the member is onboarded against, so it is required. -->
  <div class="field">
    <label>Aadhaar Number <span class="req">*</span></label>
    <input pInputText type="text" formControlName="aadhaarNumber" appDigitsOnly="12"
      inputmode="numeric" maxlength="12" placeholder="12-digit Aadhaar" />
    <!-- Told apart, because "required" and "wrong length" need different corrections. -->
    @if (form.get('aadhaarNumber')?.touched && form.get('aadhaarNumber')?.hasError('required')) {
      <small class="err">Aadhaar number is required</small>
    } @else if (form.get('aadhaarNumber')?.touched && form.get('aadhaarNumber')?.invalid) {
      <small class="err">Aadhaar must be exactly 12 digits</small>
    }
  </div>

  <!-- ===================== Contact & address ===================== -->
  <h3 class="section-title"><i class="pi pi-map-marker"></i> Contact &amp; Address</h3>

  <!-- Mobile / Email -->
  <div class="grid-2">
    <div class="field">
      <label>Mobile Number <span class="req">*</span></label>
      <input pInputText type="text" formControlName="mobileNumber" appDigitsOnly="10"
        inputmode="numeric" maxlength="10" placeholder="10-digit mobile" />
      @if (form.get('mobileNumber')?.touched && form.get('mobileNumber')?.invalid) {
        <small class="err">Enter a valid 10-digit mobile</small>
      }
    </div>
    <div class="field">
      <label>Email</label>
      <input pInputText type="email" formControlName="email" placeholder="name@example.com" />
      @if (form.get('email')?.touched && form.get('email')?.invalid) {
        <small class="err">Invalid email</small>
      }
    </div>
  </div>

  <!-- Address -->
  <div class="field">
    <label>Address <span class="req">*</span></label>
    <textarea class="form-textarea" rows="2" formControlName="address" placeholder="Residential address"></textarea>
    @if (form.get('address')?.touched && form.get('address')?.invalid) {
      <small class="err">Address is required</small>
    }
  </div>

  <!-- City / State -->
  <div class="grid-2">
    <div class="field">
      <label>City <span class="req">*</span></label>
      <input pInputText type="text" formControlName="city" placeholder="City" />
      @if (form.get('city')?.touched && form.get('city')?.invalid) {
        <small class="err">City is required</small>
      }
    </div>
    <div class="field">
      <label>State <span class="req">*</span></label>
      <input pInputText type="text" formControlName="state" placeholder="State" />
      @if (form.get('state')?.touched && form.get('state')?.invalid) {
        <small class="err">State is required</small>
      }
    </div>
  </div>

  <!-- Pincode / CFL Branch -->
  <div class="grid-2">
    <div class="field">
      <label>Pincode <span class="req">*</span></label>
      <input pInputText type="text" formControlName="pincode" appDigitsOnly="6"
        inputmode="numeric" maxlength="6" placeholder="6-digit pincode" />
      @if (form.get('pincode')?.touched && form.get('pincode')?.invalid) {
        <small class="err">Pincode must be 6 digits</small>
      }
    </div>
    <div class="field">
      <label>Branch</label>
      <input pInputText type="text" formControlName="cflBranch" placeholder="CFL branch" />
    </div>
  </div>

  <!-- Doctor consultant -->
  <div class="field">
    <div class="tc-row">
      <p-checkbox formControlName="isDoctorConsultant" [binary]="true" inputId="doc" />
      <label for="doc">Include doctor consultant</label>
    </div>
  </div>

  <!-- ===================== Premium summary (preview only) ===================== -->
  @if (hasRule) {
    <p-card styleClass="premium-card">
      <div class="premium-head">
        Premium summary <small class="hint-muted">(preview — recomputed by the server)</small>
      </div>
      <div class="premium-grid">
        <div class="premium-cell">
          <span class="k">Base premium</span>
          <span class="v">{{ form.get('basePrimume')?.value | number: '1.0-0' }}</span>
        </div>
        <div class="premium-cell">
          <span class="k">Doctor consultant</span>
          <span class="v">{{ form.get('doctorConsultantAMT')?.value | number: '1.0-0' }}</span>
        </div>
        <div class="premium-cell">
          <span class="k">GST ({{ form.get('gstPer')?.value }}%)</span>
          <span class="v">{{ form.get('gstAmt')?.value | number: '1.0-0' }}</span>
        </div>
        <div class="premium-cell total">
          <span class="k">Premium with GST</span>
          <span class="v">{{ form.get('premiumWithGST')?.value | number: '1.0-0' }}</span>
        </div>
      </div>
    </p-card>
  }

  <!-- ===================== Declarations ===================== -->
  <h3 class="section-title"><i class="pi pi-verified"></i> Declarations</h3>

  <!-- Health declaration (mandatory) -->
  <div class="field">
    <div class="tc-row">
      <p-checkbox formControlName="healthDeclaration" [binary]="true" inputId="health" />
      <label for="health">
        I declare that the member is in good health and the information provided is true and complete.
        <span class="req">*</span>
      </label>
    </div>
    @if (form.get('healthDeclaration')?.touched && form.get('healthDeclaration')?.invalid) {
      <small class="err">The health declaration is required</small>
    }
  </div>

  <!-- Terms & Conditions (mandatory) -->
  <div class="field">
    <div class="tc-row">
      <p-checkbox formControlName="termcondition" [binary]="true" inputId="tc" />
      <label for="tc">
        I accept the
        <a href="javascript:void(0)" class="tc-link" (click)="openTerms()">Terms &amp; Conditions</a>
        <span class="req">*</span>
      </label>
    </div>
    @if (form.get('termcondition')?.touched && form.get('termcondition')?.invalid) {
      <small class="err">You must accept the Terms &amp; Conditions</small>
    }
  </div>
</form>

<!-- ===================== Terms & Conditions viewer ===================== -->
<p-dialog [visible]="showTerms()" (visibleChange)="showTerms.set($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Terms & Conditions"
  styleClass="menu-dialog" [style]="{ width: '720px' }" [breakpoints]="{ '720px': '94vw' }">
  <div class="tc-content" [innerHTML]="termsHtml()"></div>
  <div class="tc-dialog-actions">
    <p-button label="Close" [outlined]="true" (onClick)="showTerms.set(false)" />
  </div>
</p-dialog>
